import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://astrokuberchat.com";
const DEFAULT_OG = `${SITE_URL}/og-cover.jpg`;

/**
 * SEO — drop into the top of any page to give it a unique title, description,
 * canonical URL, OG/Twitter tags, and (optionally) JSON-LD structured data.
 *
 * Usage:
 *   <SEO
 *     title="Free Kundali · AstroKuber"
 *     description="Generate your free North-Indian Vedic birth chart…"
 *     path="/free-kundali"
 *     keywords={["free kundali", "vedic birth chart"]}
 *     jsonLd={{ "@context": "https://schema.org", ... }}
 *   />
 */
export default function SEO({
  title,
  description,
  path = "",
  keywords = [],
  image,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const fullTitle = title?.includes("AstroKuber") ? title : `${title} · AstroKuber`;
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const og = image || DEFAULT_OG;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords?.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={og} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="AstroKuber" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={og} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export { SITE_URL };
